import React from 'react';
import { Box, Container, Typography, Grid, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import { Fade } from 'react-awesome-reveal';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';

const SERVICES = [
  {
    icon: <RestaurantIcon sx={{ fontSize: 32 }} />,
    tag: 'Estándar',
    title: 'Catering Estándar',
    desc: 'La opción más práctica. El sushi se elabora en nuestra cocina y se entrega listo para servir en bandejas. Ideal para oficinas, rodajes, formaciones y celebraciones en casa.',
    ideal: ['Oficinas', 'Formaciones', 'Casa'],
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 32 }} />,
    tag: 'Más solicitado',
    title: 'Catering Premium',
    desc: 'Transformamos tu espacio en un rincón Sushigi. Incluye mesas, vajilla, decoración y montaje completo. Nuestro equipo gestiona cada detalle del evento.',
    ideal: ['Empresas', 'Inauguraciones', 'Celebraciones'],
    highlight: true,
  },
  {
    icon: <PersonIcon sx={{ fontSize: 32 }} />,
    tag: 'Más experiencial',
    title: 'Premium + Sushiman',
    desc: 'Un chef sushiman profesional trabaja en vivo en tu evento, explicando técnicas y elaborando piezas al momento. El espectáculo y la gastronomía en uno.',
    ideal: ['Bodas', 'Lanzamientos', 'Festivales'],
  },
];

const Services: React.FC = () => {
  return (
    <Box id="services" sx={{ py: 10, background: 'var(--color-bg)' }}>
      <Container maxWidth="lg">
        <Fade triggerOnce direction="up">
          <Typography
            variant="overline"
            align="center"
            sx={{ display: 'block', color: 'var(--color-accent)', letterSpacing: '0.3em', mb: 1 }}
          >
            Nuestros formatos
          </Typography>
          <Typography
            variant="h2"
            align="center"
            sx={{ fontFamily: 'var(--font-heading)', fontSize: { xs: '1.8rem', md: '2.5rem' }, mb: 2 }}
          >
            Elige el catering que mejor se adapta a tu evento
          </Typography>
          <Typography align="center" sx={{ color: 'var(--color-text-secondary)', maxWidth: 560, mx: 'auto', mb: 8 }}>
            Tres formatos diseñados para cubrir cualquier tipo de celebración, con la calidad Sushigi garantizada.
          </Typography>
        </Fade>

        <Grid container spacing={3}>
          {SERVICES.map((s, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
              <Fade triggerOnce direction="up" delay={i * 120}>
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  style={{ height: '100%' }}
                >
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: 2,
                      border: s.highlight ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
                      bgcolor: s.highlight ? 'rgba(200,169,110,0.05)' : 'var(--color-bg-card)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      cursor: 'default',
                      transition: 'border-color 0.3s, box-shadow 0.3s',
                      ...(s.highlight && {
                        boxShadow: '0 0 40px rgba(200,169,110,0.08)',
                      }),
                      '&:hover': {
                        borderColor: 'var(--color-accent)',
                        boxShadow: '0 8px 40px rgba(200,169,110,0.12)',
                      },
                    }}
                  >
                    {/* Icon */}
                    <Box sx={{ color: 'var(--color-accent)', mb: 2.5, opacity: 0.9 }}>
                      {s.icon}
                    </Box>

                    <Chip
                      label={s.tag}
                      size="small"
                      sx={{
                        alignSelf: 'flex-start',
                        mb: 2,
                        bgcolor: s.highlight ? 'var(--color-accent)' : 'var(--color-border)',
                        color: s.highlight ? '#0d0d0d' : 'var(--color-text-secondary)',
                        fontWeight: 600,
                        fontSize: '0.68rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                      }}
                    />

                    <Typography
                      variant="h6"
                      sx={{ fontFamily: 'var(--font-heading)', color: 'var(--color-text-primary)', mb: 2, fontSize: '1.1rem' }}
                    >
                      {s.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.85, flexGrow: 1, mb: 3 }}
                    >
                      {s.desc}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {s.ideal.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)', fontSize: '0.68rem' }}
                        />
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Services;
